/**
 * Calendário de feriados do tenant.
 * NACIONAL | ESTADUAL | MUNICIPAL | FACULTATIVO | PERSONALIZADO
 * Botão "Importar nacionais" pré-popula com feriados de lei + facultativos do ano.
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { feriadoApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { Feriado } from '../../types/api'

const TIPO_LABELS: Record<string, { label: string; color: string }> = {
  NACIONAL:    { label: 'Nacional',    color: '#2563eb' },
  ESTADUAL:    { label: 'Estadual',    color: '#7c3aed' },
  MUNICIPAL:   { label: 'Municipal',   color: '#0891b2' },
  FACULTATIVO: { label: 'Facultativo', color: '#d97706' },
  PERSONALIZADO: { label: 'Personalizado', color: '#16a34a' },
}

const ANO_ATUAL = new Date().getFullYear()

const FORM_EMPTY = {
  data: '', nome: '', tipo: 'PERSONALIZADO', uf: '', municipio: '', recorrente: false,
}

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']

export function FeriadosPage() {
  const qc = useQueryClient()
  const [ano, setAno] = useState(ANO_ATUAL)
  const { data: feriados, isLoading } = useQuery({
    queryKey: ['feriados', ano],
    queryFn: () => feriadoApi.list(ano),
  })

  const [showForm, setShowForm] = useState(false)
  const [editFeriado, setEditFeriado] = useState<Feriado | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [formError, setFormError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ inseridos: number; ignorados: number } | null>(null)

  const saveMutation = useMutation({
    mutationFn: () => {
      const tipo = form.tipo as Feriado['tipo']
      const payload = { ...form, tipo, uf: form.uf || null, municipio: form.municipio || null }
      return editFeriado
        ? feriadoApi.update(editFeriado.id, payload)
        : feriadoApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['feriados'] }); closeForm() },
    onError: (err) => setFormError(getErrorMessage(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => feriadoApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feriados'] }),
  })

  const importMutation = useMutation({
    mutationFn: () => feriadoApi.importarNacionais(ano),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['feriados'] }); setImportResult(r) },
    onError: (err) => setFormError(getErrorMessage(err)),
  })

  function openCreate() { setEditFeriado(null); setForm(FORM_EMPTY); setFormError(null); setShowForm(true) }
  function openEdit(f: Feriado) {
    setEditFeriado(f)
    setForm({ data: f.data, nome: f.nome, tipo: f.tipo, uf: f.uf ?? '', municipio: f.municipio ?? '', recorrente: f.recorrente })
    setFormError(null); setShowForm(true)
  }
  function closeForm() { setShowForm(false); setEditFeriado(null) }

  // Agrupa por mês
  const porMes: Record<number, Feriado[]> = {}
  feriados?.forEach(f => {
    const m = new Date(f.data + 'T00:00:00').getMonth()
    ;(porMes[m] ??= []).push(f)
  })

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Feriados</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Calendário de feriados — integrado ao cálculo do espelho de ponto
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <button className="btn btn-ghost" onClick={() => setAno(a => a - 1)}>◀</button>
            <span style={{ fontWeight: 600, minWidth: 50, textAlign: 'center' }}>{ano}</span>
            <button className="btn btn-ghost" onClick={() => setAno(a => a + 1)}>▶</button>
          </div>
          <button
            className="btn btn-ghost"
            onClick={() => importMutation.mutate()}
            disabled={importMutation.isPending}
          >
            {importMutation.isPending ? 'Importando…' : 'Importar nacionais'}
          </button>
          <button className="btn btn-primary" onClick={openCreate}>+ Adicionar</button>
        </div>
      </div>

      {importResult && (
        <div className="alert alert-success" style={{ marginBottom: 'var(--space-4)' }}>
          Importação {ano}: {importResult.inseridos} novos feriados inseridos, {importResult.ignorados} já existentes.
          <button style={{ marginLeft: 'var(--space-3)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setImportResult(null)}>✕</button>
        </div>
      )}

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
        {Object.entries(TIPO_LABELS).map(([tipo, { label, color }]) => (
          <div key={tipo} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: color, display: 'inline-block' }} />
            {label}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
          <span>↻</span> = recorrente todo ano
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={e => { if (e.target === e.currentTarget) closeForm() }}>
          <div className="card" style={{ width: '100%', maxWidth: 480 }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{editFeriado ? 'Editar Feriado' : 'Novo Feriado'}</h2>
            {formError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{formError}</div>}
            <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveMutation.mutate() }} noValidate>
              <F label="Data">
                <input className="input" type="date" value={form.data}
                  onChange={e => setForm(f => ({ ...f, data: e.target.value }))} required />
              </F>
              <F label="Nome do feriado">
                <input className="input" value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: Aniversário da cidade" required />
              </F>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <F label="Tipo">
                  <select className="input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                    {Object.entries(TIPO_LABELS).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </F>
                <F label="UF" opt>
                  <input className="input" value={form.uf} onChange={e => setForm(f => ({ ...f, uf: e.target.value.toUpperCase() }))} maxLength={2} placeholder="SP" />
                </F>
              </div>
              <F label="Município" opt>
                <input className="input" value={form.municipio} onChange={e => setForm(f => ({ ...f, municipio: e.target.value }))} placeholder="Ex: São Paulo" />
              </F>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', marginBottom: 'var(--space-4)' }}>
                <input type="checkbox" checked={form.recorrente} onChange={e => setForm(f => ({ ...f, recorrente: e.target.checked }))} />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>Repetir na mesma data todo ano</span>
              </label>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading ? <PageLoading /> : !feriados?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>Nenhum feriado para {ano}.</p>
          <button className="btn btn-primary" onClick={() => importMutation.mutate()} disabled={importMutation.isPending}>
            Importar feriados nacionais {ano}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-4)' }}>
          {Array.from({ length: 12 }, (_, m) => ({ m, list: porMes[m] ?? [] }))
            .filter(({ list }) => list.length > 0)
            .map(({ m, list }) => (
              <div key={m} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  {MESES[m]}
                </div>
                {list.map(f => {
                  const tipo = TIPO_LABELS[f.tipo] ?? { label: f.tipo, color: '#6b7280' }
                  const d = new Date(f.data + 'T00:00:00')
                  return (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-2) var(--space-4)', borderBottom: '1px solid var(--color-gray-100)', gap: 'var(--space-3)' }}>
                      <span style={{ minWidth: 32, fontWeight: 600, color: 'var(--color-gray-700)', fontSize: 'var(--font-size-sm)' }}>
                        {String(d.getDate()).padStart(2, '0')}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.recorrente && <span title="Recorrente todo ano" style={{ marginRight: 4, color: 'var(--color-gray-400)' }}>↻</span>}
                          {f.nome}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: tipo.color, fontWeight: 500 }}>{tipo.label}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: '2px 6px' }} onClick={() => openEdit(f)}>✏</button>
                        <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: '2px 6px', color: 'var(--color-danger)' }}
                          onClick={() => { if (confirm(`Remover ${f.nome}?`)) deleteMutation.mutate(f.id) }}>✕</button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

function F({ label, children, opt }: { label: string; children: React.ReactNode; opt?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{opt && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}
